/**
 * What a score is, as opposed to what is written in it: its title, its parts, how many bars
 * it has, and the metre, key and tempo in force.
 *
 * Every control here builds a command from `@app/score-editor`'s `setup` namespace and hands
 * it to the editor, so a change made in this panel is one undo step and goes through exactly
 * the path a script would. Nothing is decided locally.
 *
 * Signatures are stated at the bar the caret stands in, not always at the first bar, because
 * a metre or key change partway through a piece is ordinary and an editor that could only
 * change bar 1 would be a toy. The heading says which bar is being written to.
 */

import { measureIndexOf, resolveAttributes, setup } from "@app/score-editor";
import type {
  Command,
  Duration,
  KeySignature,
  MeasureId,
  Score,
  TimeSignature,
} from "@app/score-editor";
import { Kicker } from "./Page";
import { Choice, Count, Text } from "./ScoreSetupFields";
import type { Option } from "./ScoreSetupFields";
import ScoreParts from "./ScoreParts";

/** Tempo marks are written in quarter notes here; the document allows any note value. */
const QUARTER: Duration = { type: "quarter", dots: 0, tuplet: null };

/** What the playback engine counts when a score states no tempo at all. */
const DEFAULT_BPM = 120;

const MAJOR = "C♭ G♭ D♭ A♭ E♭ B♭ F C G D A E B F♯ C♯".split(" ");
const MINOR = "a♭ e♭ b♭ f c g d a e b f♯ c♯ g♯ d♯ a♯".split(" ");

const KEYS: readonly Option<string>[] = MAJOR.map((major, index) => ({
  value: String(index - 7),
  label: `${major} major / ${MINOR[index] ?? ""} minor`,
}));

const MODES: readonly Option<string>[] = [
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" },
];

/** The metres a picker offers. A score in anything else keeps what it has, listed first. */
const TIMES: readonly TimeSignature[] = [
  { beats: 4, beatType: 4, symbol: "common" },
  { beats: 3, beatType: 4, symbol: "number" },
  { beats: 2, beatType: 4, symbol: "number" },
  { beats: 2, beatType: 2, symbol: "cut" },
  { beats: 3, beatType: 2, symbol: "number" },
  { beats: 5, beatType: 4, symbol: "number" },
  { beats: 6, beatType: 8, symbol: "number" },
  { beats: 7, beatType: 8, symbol: "number" },
  { beats: 9, beatType: 8, symbol: "number" },
  { beats: 12, beatType: 8, symbol: "number" },
];

function timeKey(time: TimeSignature): string {
  return `${time.beats}/${time.beatType}`;
}

export interface ScoreSetupProps {
  readonly score: Score;
  /** The bar the caret stands in. Signatures are stated there; `null` means the first bar. */
  readonly at: MeasureId | null;
  /** Takes the command a setup function built, or the sentence saying why there is none. */
  edit(built: Command | string, label?: string): void;
}

export default function ScoreSetup({ score, at, edit }: ScoreSetupProps): JSX.Element {
  const apply = edit;

  const first = score.measures[0]?.id ?? null;
  const bar = at ?? first;
  const index = bar === null ? 0 : Math.max(measureIndexOf(score, bar), 0);
  const { time, key, tempo } = resolveAttributes(score, index);

  const stated = (make: (id: MeasureId) => Command | string, label: string): void => {
    apply(bar === null ? "the score has no bars" : make(bar), label);
  };

  const setKey = (next: KeySignature): void =>
    stated((id) => setup.setKeyAt(score, id, next), "change the key");

  const times = TIMES.some((known) => timeKey(known) === timeKey(time)) ? TIMES : [time, ...TIMES];

  const setBars = (count: number): void => {
    const have = score.measures.length;
    if (count > have) apply(setup.addMeasures(score, count - have), "add bars");
    else if (count < have) apply(setup.removeMeasures(score, have - count), "remove bars");
  };

  return (
    <details className="rounded-xl border border-rule bg-white px-4 py-3">
      <summary className="cursor-pointer select-none font-mono text-[11px] uppercase tracking-[0.14em] text-ink-quiet">
        Set up the score
      </summary>

      <div className="mt-4 space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <Text
            label="Title"
            value={score.meta.title}
            className="min-w-[200px] flex-1"
            onCommit={(title) => apply({ kind: "setMeta", meta: { title } }, "rename the score")}
          />
          <Text
            label="Composer"
            value={score.meta.composer ?? ""}
            placeholder="Anonymous"
            className="min-w-[160px] flex-1"
            onCommit={(written) =>
              apply(
                { kind: "setMeta", meta: { composer: written.trim() === "" ? null : written } },
                "name the composer",
              )
            }
          />
          <Count
            label="Bars"
            value={score.measures.length}
            min={1}
            max={400}
            className="w-[88px]"
            onCommit={setBars}
          />
        </div>

        <div className="space-y-2">
          <Kicker>From bar {index + 1}</Kicker>
          <div className="flex flex-wrap items-end gap-3">
            <Choice
              label="Metre"
              value={timeKey(time)}
              options={times.map((known) => ({ value: timeKey(known), label: timeKey(known) }))}
              className="w-[110px]"
              onChange={(picked) => {
                const chosen = times.find((known) => timeKey(known) === picked);
                if (chosen === undefined) return;
                stated((id) => setup.setTimeAt(score, id, chosen), "change the metre");
              }}
            />
            <Choice
              label="Key"
              value={String(key.fifths)}
              options={KEYS}
              className="w-[190px]"
              onChange={(fifths) => setKey({ ...key, fifths: Number(fifths) })}
            />
            <Choice
              label="Mode"
              value={key.mode ?? "major"}
              options={MODES}
              className="w-[110px]"
              onChange={(mode) => setKey({ ...key, mode: mode === "minor" ? "minor" : "major" })}
            />
            <Count
              label="♩ per minute"
              value={tempo?.bpm ?? DEFAULT_BPM}
              min={20}
              max={320}
              className="w-[110px]"
              onCommit={(bpm) =>
                stated(
                  (id) => setup.setTempoAt(score, id, { bpm, unit: QUARTER, text: null }),
                  "change the tempo",
                )
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Kicker>Parts</Kicker>
          <ScoreParts score={score} apply={apply} />
        </div>
      </div>
    </details>
  );
}
